import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ClientReportRequestSummary, ClientRequestStatusFilter } from '@/types/report-request';

/**
 * Client-facing report requests — the /requests tab. Team scoping is
 * server-side; these hooks page (and search/filter) the caller's rows.
 * `q` should already be settled/debounced by the caller — every value
 * change is a fetch.
 */

export function useClientReportRequests(
  params: {
    page?: number;
    limit?: number;
    q?: string;
    status?: ClientRequestStatusFilter;
  } = {}
) {
  const { page = 1, limit = 25, q, status } = params;
  const [items, setItems] = useState<ClientReportRequestSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listClientReportRequests({ page, limit, q, status });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load report requests');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, q, status]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { items, total, isLoading, error, refetch: fetchOnce };
}

export function useClientReportRequest(requestId: string | undefined) {
  const [request, setRequest] = useState<ClientReportRequestSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!requestId) {
      setRequest(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setRequest(await vnextApiClient.getClientReportRequest(requestId));
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load the report request');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  // The updated summary comes back on the PATCH — no refetch round-trip.
  const updateReference = useCallback(
    async (value: string | null) => {
      if (!requestId) {
        return;
      }
      setRequest(
        await vnextApiClient.updateClientReportRequest(requestId, {
          client_reference: value,
        })
      );
    },
    [requestId]
  );

  return { request, isLoading, error, refetch: fetchOnce, updateReference };
}
